import React, { useState, useEffect, useRef } from 'react';
import { Zap, Plus, X, Trash2 } from 'lucide-react';

const QuickReplies = ({ onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newReply, setNewReply] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('whatsapp_quick_replies');
    if (saved) {
      try {
        setReplies(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse quick replies', e);
      }
    } else {
      // Default replies
      const defaults = [
        "Hello! How can I help you today?",
        "I'll check on this and get back to you shortly.",
        "Thank you for contacting us!",
        "Could you please provide more details?"
      ];
      setReplies(defaults);
      localStorage.setItem('whatsapp_quick_replies', JSON.stringify(defaults));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsAdding(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const saveReplies = (newReplies) => {
    setReplies(newReplies);
    localStorage.setItem('whatsapp_quick_replies', JSON.stringify(newReplies));
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;
    saveReplies([...replies, newReply.trim()]);
    setNewReply('');
    setIsAdding(false);
  };

  const handleDelete = (e, index) => {
    e.stopPropagation();
    const filtered = replies.filter((_, i) => i !== index);
    saveReplies(filtered);
  };

  return (
    <div style={{ position: 'relative' }} ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: isOpen ? '#cbd5e1' : '#e2e8f0',
          border: 'none',
          cursor: 'pointer',
          color: '#64748b',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s',
          flexShrink: 0
        }}
        title="Quick Replies"
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#cbd5e1' }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = '#e2e8f0' }}
      >
        <Zap size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: '50px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '320px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '400px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={16} color="#eab308" /> Quick Replies
            </h4>
            <button
              onClick={() => setIsAdding(!isAdding)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              {isAdding ? <X size={14} /> : <Plus size={14} />}
              {isAdding ? 'Cancel' : 'Add'}
            </button>
          </div>

          {isAdding && (
            <form onSubmit={handleAdd} style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <textarea
                value={newReply}
                onChange={e => setNewReply(e.target.value)}
                placeholder="Type a new quick reply..."
                autoFocus
                rows={2}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', resize: 'vertical' }}
              />
              <button
                type="submit"
                disabled={!newReply.trim()}
                style={{ marginTop: '8px', width: '100%', padding: '6px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: !newReply.trim() ? 0.6 : 1 }}
              >
                Save Reply
              </button>
            </form>
          )}

          <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
            {replies.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                No quick replies saved. Add one to save time!
              </div>
            ) : (
              replies.map((reply, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    onSelect(reply);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '4px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.4', flex: 1, marginRight: '8px', wordBreak: 'break-word' }}>
                    {reply}
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: '2px', flexShrink: 0 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickReplies;
